<script setup lang="ts">
import { Head, router, useForm, usePage } from '@inertiajs/vue3'
import { computed, onMounted, ref } from 'vue'

import DobInput from '@/components/DobInput.vue'
import InputError from '@/components/InputError.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useRecaptcha } from '@/composables/useRecaptcha'
import LandingLayout from '@/layouts/LandingLayout.vue'
import { applyPageCheckPath, applyPageResumePath } from '@/routes'

const props = defineProps<{
  genders: string[]
  recaptcha_site_key?: string | null
}>()

const { getToken } = useRecaptcha(props.recaptcha_site_key)

const form = useForm({
  first_name: '',
  last_name: '',
  middle_name: '',
  gender: '',
  date_of_birth: '',
  email: '',
  phone: '',
  consent_privacy: false,
  consent_credit_guide: false,
  consent_electronic_docs: false,
  consent_credit_report: false,
})

const consentError = ref(false)
const page = usePage()
const flashError = computed(() => {
  const e = (page.props as Record<string, unknown>).errors
  return typeof e === 'string' ? e : undefined
})

onMounted(() => {
  const customerId = localStorage.getItem('tlc_customer_id')
  const applicationId = localStorage.getItem('tlc_application_id')
  if (customerId || applicationId) {
    // Clear before the server trip — if resume fails and redirects back here,
    // localStorage is already clean so there is no redirect loop.
    // If resume succeeds, the destination page re-sets the IDs on its own onMounted.
    localStorage.removeItem('tlc_customer_id')
    localStorage.removeItem('tlc_application_id')
    router.visit(
      applyPageResumePath({
        customer_id: customerId ?? undefined,
        application_id: applicationId ?? undefined,
      }),
    )
  }
})

async function submit() {
  form.clearErrors()
  if (
    !form.consent_privacy ||
    !form.consent_credit_guide ||
    !form.consent_electronic_docs ||
    !form.consent_credit_report
  ) {
    consentError.value = true
    return
  }
  consentError.value = false
  const recaptcha_token = await getToken('apply_step1')
  form.transform((data) => ({ ...data, recaptcha_token })).post(applyPageCheckPath())
}
</script>

<template>
  <Head title="Apply for a Loan" />
  <LandingLayout>
    <div class="mb-6">
      <h1 class="text-2xl font-bold">Apply for a Loan</h1>
      <p class="text-muted-foreground mt-1 text-sm">Step 1 of 3 — Your personal details</p>
    </div>

    <form @submit.prevent="submit" novalidate class="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle class="text-base">Personal Information</CardTitle>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="first_name">First Name <span class="text-destructive">*</span></Label>
              <Input
                id="first_name"
                v-model="form.first_name"
                name="first_name"
                autocomplete="given-name"
                placeholder="John"
                :class="form.errors.first_name && 'border-destructive focus-visible:ring-destructive'"
              />
              <InputError :messages="form.errors.first_name" />
            </div>
            <div class="grid gap-2">
              <Label for="last_name">Last Name <span class="text-destructive">*</span></Label>
              <Input
                id="last_name"
                v-model="form.last_name"
                name="last_name"
                autocomplete="family-name"
                placeholder="Smith"
                :class="form.errors.last_name && 'border-destructive focus-visible:ring-destructive'"
              />
              <InputError :messages="form.errors.last_name" />
            </div>
          </div>
          <div class="grid gap-2">
            <Label for="middle_name">Middle Name</Label>
            <Input
              id="middle_name"
              v-model="form.middle_name"
              name="middle_name"
              autocomplete="additional-name"
              placeholder="Optional"
            />
          </div>
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="gender">Gender <span class="text-destructive">*</span></Label>
              <Select v-model="form.gender">
                <SelectTrigger id="gender" :class="['w-full', form.errors.gender && 'border-destructive focus-visible:ring-destructive']"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="g in props.genders" :key="g" :value="g">
                    {{ g.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.gender" />
            </div>
            <div class="grid gap-2">
              <Label for="date_of_birth">Date of Birth <span class="text-destructive">*</span></Label>
              <DobInput id="date_of_birth" v-model="form.date_of_birth" :invalid="!!form.errors.date_of_birth" />
              <InputError :messages="form.errors.date_of_birth" />
            </div>
          </div>
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="email">Email <span class="text-destructive">*</span></Label>
              <Input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                autocomplete="email"
                placeholder="john@example.com"
                :class="form.errors.email && 'border-destructive focus-visible:ring-destructive'"
              />
              <InputError :messages="form.errors.email" />
            </div>
            <div class="grid gap-2">
              <Label for="phone">Phone <span class="text-destructive">*</span></Label>
              <PhoneInput id="phone" v-model="form.phone" :invalid="!!form.errors.phone" />
              <InputError :messages="form.errors.phone" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div class="space-y-5 px-2 sm:px-0">
        <div>
          <p class="text-sm font-semibold">Acknowledgements &amp; Consents</p>
          <p class="text-muted-foreground mt-1 text-sm">
            Please read and agree to all of the following before proceeding.
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex gap-3 sm:gap-4">
            <Checkbox
              id="consent_privacy"
              :modelValue="form.consent_privacy"
              class="mt-0.5 shrink-0"
              @update:modelValue="form.consent_privacy = !!$event"
            />
            <label for="consent_privacy" class="cursor-pointer text-sm leading-relaxed">
              I have accessed and read, and agree to the acknowledgements and consents set out in, the
              <a class="text-primary font-medium underline" href="https://tlc.com.au/privacy-policy" target="_blank">Privacy Disclosure Statement</a>.
            </label>
          </div>

          <div class="flex gap-3 sm:gap-4">
            <Checkbox
              id="consent_credit_guide"
              :modelValue="form.consent_credit_guide"
              class="mt-0.5 shrink-0"
              @update:modelValue="form.consent_credit_guide = !!$event"
            />
            <label for="consent_credit_guide" class="cursor-pointer text-sm leading-relaxed">
              I have accessed and read the
              <a class="text-primary font-medium underline" href="https://tlc.com.au/credit-guide" target="_blank">Credit Guide / Quote</a>
              document and accept the terms of the Quote.
            </label>
          </div>

          <div class="flex gap-3 sm:gap-4">
            <Checkbox
              id="consent_electronic_docs"
              :modelValue="form.consent_electronic_docs"
              class="mt-0.5 shrink-0"
              @update:modelValue="form.consent_electronic_docs = !!$event"
            />
            <label for="consent_electronic_docs" class="cursor-pointer text-sm leading-relaxed">
              I consent to receiving documents (including the Credit Guide/Quote and Credit Proposal documents) by e-mail
              and acknowledge that: (a) paper documents may no longer be given; (b) electronic communications must be
              regularly checked for documents; and (c) my consent to the giving of documents by electronic communication
              may be withdrawn at any time.
            </label>
          </div>

          <div class="flex gap-3 sm:gap-4">
            <Checkbox
              id="consent_credit_report"
              :modelValue="form.consent_credit_report"
              class="mt-0.5 shrink-0"
              @update:modelValue="form.consent_credit_report = !!$event"
            />
            <label for="consent_credit_report" class="cursor-pointer text-sm leading-relaxed">
              I authorise Total Lifestyle Credit Pty Limited to act as my agent and contact a credit reporting body on my
              behalf to access my credit report. This will not affect your credit score.
            </label>
          </div>
        </div>

        <p v-if="consentError" class="text-destructive text-sm">
          You must agree to all of the above before proceeding.
        </p>
      </div>

      <div
        v-if="flashError"
        class="bg-destructive/10 border-destructive/20 text-destructive rounded-md border px-4 py-3 text-sm"
      >
        {{ flashError }}
      </div>

      <Button type="submit" class="w-full" :disabled="form.processing"> Continue to Loan Details </Button>
    </form>
  </LandingLayout>
</template>
